<template>
  <div :style="{width:'100%', maxWidth: maxWidth}">
    <svg :viewBox="`0 0 ${scaledSize} ${scaledSize}`" :width="'100%'" height="auto" role="img">
      <!-- Background circles for visual depth -->
      <defs>
        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="2" dy="2" stdDeviation="3" flood-opacity="0.3"/>
        </filter>
      </defs>
      
      <!-- CAPABILITY CIRCLES -->
      <g v-for="(circle, i) in circleLayout" :key="i">
        <!-- Main circle -->
        <circle
          :cx="circle.x"
          :cy="circle.y"
          :r="circle.radius"
          :fill="circle.color"
          :opacity="selectedItems.includes(circle.originalIndex) ? 0.95 : 0.8"
          :stroke="selectedItems.includes(circle.originalIndex) ? '#fff' : 'none'"
          :stroke-width="selectedItems.includes(circle.originalIndex) ? 3 : 0"
          style="cursor:pointer; transition: all 0.3s ease"
          filter="url(#shadow)"
          @click="onPick(i, circle)"
          @mouseenter="onHover(i)"
          @mouseleave="onHover(null)"
        />
        
        <!-- Selection indicator (outer ring) -->
        <circle
          v-if="selectedItems.includes(circle.originalIndex)"
          :cx="circle.x"
          :cy="circle.y"
          :r="circle.radius + 8"
          fill="none"
          :stroke="circle.color"
          stroke-width="2"
          opacity="0.6"
          style="transition: all 0.3s ease"
        />
        
        <!-- Capability code -->
        <text
          :x="circle.x"
          :y="circle.y - 15"
          text-anchor="middle"
          dominant-baseline="middle"
          fill="white"
          :font-size="circle.codeFontSize"
          font-weight="800"
          style="pointer-events:none"
        >
          {{ circle.code }}
        </text>
        
        <!-- Capability label - First line -->
        <text
          :x="circle.x"
          :y="circle.y + 2"
          text-anchor="middle"
          dominant-baseline="middle"
          fill="white"
          :font-size="circle.labelFontSize"
          font-weight="600"
          style="pointer-events:none"
        >
          {{ circle.firstLine }}
        </text>
        
        <!-- Capability label - Second line -->
        <text
          v-if="circle.secondLine"
          :x="circle.x"
          :y="circle.y + 15"
          text-anchor="middle"
          dominant-baseline="middle"
          fill="white"
          :font-size="circle.labelFontSize"
          font-weight="600"
          style="pointer-events:none"
        >
          {{ circle.secondLine }}
        </text>
      </g>

      <!-- RADIAL BANDS FOR CATEGORIES -->
      <!-- Knowing band (purple) - covers K1 and K2 positions -->
      <path :d="getRadialBandPath('knowing')" fill="#7A3C9A" fill-opacity="0.5" stroke="none"/>
      
      <!-- Being band (teal) - covers B1 and B2 positions -->  
      <path :d="getRadialBandPath('being')" fill="#3DB09E" fill-opacity="0.5" stroke="none"/>
      
      <!-- Doing band (blue) - covers D1, D2, D3 positions -->
      <path :d="getRadialBandPath('doing')" fill="#3F95C8" fill-opacity="0.7" stroke="none"/>
      
      <!-- Define circular paths for curved text -->
      <defs>
        <path :id="`knowing-path-${_uid}`" :d="getCurvedTextPath('knowing')" />
        <path :id="`being-path-${_uid}`" :d="getCurvedTextPath('being')" />
        <path :id="`doing-path-${_uid}`" :d="getCurvedTextPath('doing')" />
      </defs>
      
      <!-- Curved Category Labels -->
      <text :font-size="scaledHubTitleSize * 0.8" fill="white" font-weight="800" 
            style="pointer-events:none; text-shadow: 1px 1px 2px rgba(0,0,0,0.5)" text-anchor="middle">
        <textPath :href="`#knowing-path-${_uid}`" startOffset="50%">
          Knowing
        </textPath>
      </text>
      
      <text :font-size="scaledHubTitleSize * 0.8" fill="white" font-weight="800" 
            style="pointer-events:none; text-shadow: 1px 1px 2px rgba(0,0,0,0.5)" text-anchor="middle">
        <textPath :href="`#being-path-${_uid}`" startOffset="50%">
          Being
        </textPath>
      </text>
      
      <text :font-size="scaledHubTitleSize * 0.8" fill="white" font-weight="800" 
            style="pointer-events:none; text-shadow: 1px 1px 2px rgba(0,0,0,0.5)" text-anchor="middle">
        <textPath :href="`#doing-path-${_uid}`" startOffset="57%" side="right">
          Doing
        </textPath>
      </text>

      <!-- CENTER HUB -->
      <circle :cx="centerX" :cy="centerY" :r="scaledHubRadius" fill="#fff" stroke="#ddd" :stroke-width="scaledStrokeWidth" filter="url(#shadow)"/>
      <text :x="centerX" :y="centerY - (15 * scale)" text-anchor="middle" dominant-baseline="middle"
            :font-size="scaledHubTitleSize" fill="#403272" font-weight="800" style="pointer-events:none">
        Palliative Care
      </text>
      <text :x="centerX" :y="centerY" text-anchor="middle" dominant-baseline="middle"
            :font-size="scaledHubTitleSize" fill="#403272" font-weight="800" style="pointer-events:none">
        Capabilities  
      </text>
      <text :x="centerX" :y="centerY + (20 * scale)" text-anchor="middle" dominant-baseline="middle"
            :font-size="scaledHubSubSize" fill="#2f7fa7" font-weight="600" style="pointer-events:none">
        People with
      </text>
      <text :x="centerX" :y="centerY + (32 * scale)" text-anchor="middle" dominant-baseline="middle"
            :font-size="scaledHubSubSize" fill="#2f7fa7" font-weight="600" style="pointer-events:none">
        palliative care needs
      </text>
    </svg>
  </div>
</template>

<script>
import { flip } from 'lodash'

export default {
  name: 'CapabilityDonut',
  props: {
    size: { type: Number, default: 500 }, // Base size reduced by 30% (was 1000) - makes internal elements 30% larger
    scale: { type: Number, default: 1 }, // Scale factor for larger display
    outerRadius: { type: Number, default: 460 }, // Base values, will be scaled
    innerRadius: { type: Number, default: 230 },
    hubRadius: { type: Number, default: 100 },
    groupBand:   { type: Number, default: 80 },
    gapDegrees:  { type: Number, default: 3 },
    startAngle:  { type: Number, default: -90 },
    gapColor:    { type: String, default: '#ffffff' },
    gapPx:       { type: Number, default: 6 },
    maxWidth:    { type: String, default: '100%' },
    hubTitle:    { type: String, default: 'Palliative Care\\nCapabilities' },
    hubSub:      { type: String, default: 'People with palliative care needs' },
    labelFontPx: { type: Number, default: 11 }, // Base font sizes
    groupFontPx: { type: Number, default: 7 },
    hubTitlePx:  { type: Number, default: 16 },
    hubSubPx:    { type: Number, default: 12 },
    groups: {
      type: Array,
      default: () => ([
        { name: 'Knowing', color: '#7A3C9A', textColor:'#fff', flip: false },
        { name: 'Being',   color: '#3DB09E', textColor:'#fff', flip: false },
        { name: 'Doing',   color: '#3F95C8', textColor:'#fff', flip: true },
      ])
    },
    items: {
      type: Array,
      default: () => ([
        { label: 'Principles & Values', group: 0, index: 0 },
        { label: 'Unique Responses', group: 0, index: 1 },
        { label: 'Communication', group: 2, index: 2 },
        { label: 'Inclusive Care', group: 2, index: 3 },
        { label: 'Collaborative Care', group: 2, index: 4 },
        { label: 'Legislation & Frameworks', group: 1, index: 5 },
        { label: 'Ongoing Development', group: 1, index: 6 },
      ])
    },
    value: { type: Array, default: () => [] }, // Selected indices (array for multi-select, number for single select)
    groupOpacity: { type: Number, default: 0.25 }
  },
  data () {
    return { 
      selectedItems: Array.isArray(this.value) ? [...this.value] : (this.value !== -1 ? [this.value] : [])
    }
  },
  watch: { 
    value(v) { 
      this.selectedItems = Array.isArray(v) ? [...v] : (v !== -1 ? [v] : [])
    }
  },
  computed: {
    // Dynamic scaling based on scale prop
    scaledSize() { return this.size * this.scale },
    scaledHubRadius() { return this.hubRadius * this.scale },
    scaledStrokeWidth() { return 2 * this.scale },
    scaledHubTitleSize() { return this.hubTitlePx * this.scale },
    scaledHubSubSize() { return this.hubSubPx * this.scale },
    scaledLabelFontSize() { return this.labelFontPx * this.scale },
    scaledCodeFontSize() { return 16 * this.scale }, // Base code font size
    
    centerX() { return this.scaledSize / 2 },
    centerY() { return this.scaledSize / 2 },
    
    sliceColors(){
      const tinter = (hex, f=0.12) => {
        const c = hex.replace('#','')
        const r=parseInt(c.substr(0,2),16), g=parseInt(c.substr(2,2),16), b=parseInt(c.substr(4,2),16)
        const tint = (v)=>Math.round(v + (255-v)*f)
        return `#${[tint(r),tint(g),tint(b)].map(n=>n.toString(16).padStart(2,'0')).join('')}`
      }
      const map = {}
      this.groups.forEach((g,gi)=>{ map[gi]=g.color })
      return map
    },
    
    circleLayout() {
      const circles = []
      const center = { x: this.centerX, y: this.centerY }
      const radius = 80 * this.scale // Dynamically scaled circle radius
      const centerOffset = 160 * this.scale // Dynamically scaled distance from center
      
      // Define positions for 7 circles using algorithmic spacing (360°/7 = ~51.43°)
      const angleStep = (2 * Math.PI) / 7 // Convert to radians
      const startAngle = Math.PI / 2 // Start from bottom (6 o'clock position) for D2
      
      const positions = []
      for (let i = 0; i < 7; i++) {
        const angle = startAngle + (i * angleStep)
        positions.push({
          x: center.x + centerOffset * Math.cos(angle),
          y: center.y + centerOffset * Math.sin(angle),
          radius
        })
      }
      
      // Map specific capabilities to positions starting from D2 at bottom center
      const capabilityOrder = [
        'Inclusive Care',         // D2 - 
        'Communication',          // D1 - bottom left  
        'Unique Responses',       // K2 - top left
        'Principles & Values',    // K1 - top center
        'Legislation & Frameworks', // B1 - top right     
        'Ongoing Development',     // B2 - right  
        'Collaborative Care',     // D3 - left      
      ]
      
      capabilityOrder.forEach((capabilityName, index) => {
        const item = this.items.find(item => item.label === capabilityName)
        if (item && positions[index]) {
          const pos = positions[index]
          const groupColor = this.sliceColors[item.group] || '#3F95C8'
          const textLines = this.splitLabelText(item.label)
          
          circles.push({
            x: pos.x,
            y: pos.y,
            radius: pos.radius,
            color: groupColor,
            code: this.getCapabilityCode(item.label),
            firstLine: textLines.first,
            secondLine: textLines.second,
            shortLabel: this.getShortLabel(item.label),
            fullLabel: item.label,
            codeFontSize: this.scaledCodeFontSize,
            labelFontSize: this.scaledLabelFontSize,
            group: item.group,
            originalIndex: item.index // Keep track of original index for selection
          })
        }
      })
      
      return circles
    }
  },
  methods: {
    getCapabilityCode(label) {
      const codeMap = {
        'Principles & Values': 'K1',
        'Unique Responses': 'K2', 
        'Communication': 'D1',
        'Inclusive Care': 'D2',
        'Collaborative Care': 'D3',
        'Legislation & Frameworks': 'B1',
        'Ongoing Development': 'B2'
      }
      return codeMap[label] || 'C'
    },
    
    getShortLabel(label) {
      const shortMap = {
        'Principles & Values': 'Principles',
        'Unique Responses': 'Responses',
        'Communication': 'Communicate',
        'Inclusive Care': 'Inclusive',
        'Collaborative Care': 'Collaborate',
        'Legislation & Frameworks': 'Legislation',
        'Ongoing Development': 'Development'
      }
      return shortMap[label] || label.split(' ')[0]
    },
    
    splitLabelText(label) {
      // Split labels into two lines based on common patterns
      const splitMappings = {
        'Principles & Values': { first: 'Principles', second: '& Values' },
        'Unique Responses': { first: 'Unique', second: 'Responses' },
        'Communication': { first: 'Communication', second: null },
        'Inclusive Care': { first: 'Inclusive', second: 'Care' },
        'Collaborative Care': { first: 'Collaborative', second: 'Care' },
        'Legislation & Frameworks': { first: 'Legislation', second: '& Frameworks' },
        'Ongoing Development': { first: 'Ongoing', second: 'Development' },
      }
      
      return splitMappings[label] || { first: label, second: null }
    },
    
    onHover(index) {
      // Optional: Add hover effects
      console.log('Hover:', index)
    },
    
    onPick(i, circle){
      const originalIndex = circle.originalIndex
      // Toggle selection
      if (this.selectedItems.includes(originalIndex)) {
        // Remove from selection
        this.selectedItems = this.selectedItems.filter(index => index !== originalIndex)
      } else {
        // Add to selection
        this.selectedItems = [...this.selectedItems, originalIndex]
      }
      
      // Emit the updated selection array
      this.$emit('input', this.selectedItems)
      this.$emit('select', { 
        selectedIndices: this.selectedItems,
        toggledIndex: originalIndex, 
        toggledItem: this.items[originalIndex], 
        circle,
        isSelected: this.selectedItems.includes(originalIndex)
      })
    },
    
    getRadialBandPath(category) {
      const center = { x: this.centerX, y: this.centerY }
      const innerRadius = this.scaledHubRadius + (5 * this.scale)
      const outerRadius = this.scaledHubRadius + (25 * this.scale)
      
      // Match the actual bubble positions: start at 90° (bottom), step by 51.43°
      const angleStep = 360 / 7 // 51.43 degrees
      const startAngle = 90 // Start from bottom (same as bubbles)
      
      // Capability order from circleLayout:
      // 0: D2 (Inclusive Care) - bottom center (90°)
      // 1: D1 (Communication) - bottom left (141.43°) 
      // 2: D3 (Collaborative Care) - left (192.86°)
      // 3: K2 (Unique Responses) - top left (244.29°)
      // 4: K1 (Principles & Values) - top center (295.72°)
      // 5: B1 (Legislation & Frameworks) - top right (347.15°)  
      // 6: B2 (Ongoing Development) - right (38.58°)
      
      const angleRanges = {
        // Doing: positions 0, 1, 2 (D2, D1, D3) - extended to cover the extra bubble width
        'doing': { 
          start: startAngle - (angleStep * 1.5), // Rotated back by one bubble position
          span: angleStep * 3 // Extended to reach just before K2 (covers D2, D1, D3 plus extra gap)
        },
        // Knowing: positions 3, 4 (K2, K1) - rotated anti-clockwise by one bubble  
        'knowing': { 
          start: startAngle + (angleStep * 1.5), // Rotated back by one bubble position
          span: angleStep * 2 // Cover K2, K1 plus margins
        },
        // Being: positions 5, 6 (B1, B2) - rotated anti-clockwise by one bubble
        'being': { 
          start: startAngle + (angleStep * 3.5), // Rotated back by one bubble position
          span: angleStep * 2 // Cover B1, B2 plus margins
        }
      }
      
      const range = angleRanges[category]
      if (!range) return ''
      
      const startAngleRad = (range.start * Math.PI / 180)
      const endAngleRad = ((range.start + range.span) * Math.PI / 180)
      
      // Calculate arc points
      const x1 = center.x + innerRadius * Math.cos(startAngleRad)
      const y1 = center.y + innerRadius * Math.sin(startAngleRad)
      const x2 = center.x + outerRadius * Math.cos(startAngleRad)
      const y2 = center.y + outerRadius * Math.sin(startAngleRad)
      
      const x3 = center.x + outerRadius * Math.cos(endAngleRad)
      const y3 = center.y + outerRadius * Math.sin(endAngleRad)
      const x4 = center.x + innerRadius * Math.cos(endAngleRad)
      const y4 = center.y + innerRadius * Math.sin(endAngleRad)
      
      const largeArc = range.span > 180 ? 1 : 0
      
      return `M ${x1} ${y1} 
              L ${x2} ${y2} 
              A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${x3} ${y3}
              L ${x4} ${y4} 
              A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x1} ${y1} Z`
    },
    
    getCategoryLabelPosition(category) {
      const center = { x: this.centerX, y: this.centerY }
      const radius = this.scaledHubRadius + (15 * this.scale) // Middle of the band
      
      // Position labels in the middle of each category's actual band
      const angleStep = 360 / 7 // 51.43 degrees
      const startAngle = 90 // Start from bottom (same as bubbles)
      
      const midAngles = {
        // Doing: rotated anti-clockwise by one bubble position
        'doing': startAngle + (angleStep * 0), // Rotated back by one position (90°)
        // Knowing: rotated anti-clockwise by one bubble position  
        'knowing': startAngle + (angleStep * 2.5), // Rotated back by one position (218.575°)
        // Being: rotated anti-clockwise by one bubble position
        'being': startAngle + (angleStep * 4.5), // Rotated back by one position (321.43°)
      }
      
      const angle = (midAngles[category] * Math.PI / 180)
      
      return {
        x: center.x + radius * Math.cos(angle),
        y: center.y + radius * Math.sin(angle)
      }
    },
    
    getCurvedTextPath(category) {
      const center = { x: this.centerX, y: this.centerY }
      // Adjust radius based on whether text is flipped
      const group = this.groups.find(g => g.name.toLowerCase() === category)
      const shouldFlip = group ? group.flip : false
      const radius = this.scaledHubRadius + (shouldFlip ? 15 : 10) * this.scale // More spacing above for flipped text
      
      // Position curves to follow the band paths
      const angleStep = 360 / 7 // 51.43 degrees
      const startAngle = 90 // Start from bottom (same as bubbles)
      
      // Calculate the span for each category's curve
      const curveSpans = {
        'doing': {
          start: startAngle - (angleStep * 1.5), // Start of doing band
          span: angleStep * 3.5 // Span of doing band
        },
        'knowing': {
          start: startAngle + (angleStep * 1.5), // Start of knowing band
          span: angleStep * 2 // Span of knowing band
        },
        'being': {
          start: startAngle + (angleStep * 3.5), // Start of being band  
          span: angleStep * 2 // Span of being band
        }
      }
      
      const curve = curveSpans[category]
      if (!curve) return ''
      
      // If flipped, reverse the path direction
      let startAngleRad, endAngleRad
      if (shouldFlip) {
        startAngleRad = ((curve.start + curve.span) * Math.PI / 180)
        endAngleRad = (curve.start * Math.PI / 180)
      } else {
        startAngleRad = (curve.start * Math.PI / 180)
        endAngleRad = ((curve.start + curve.span) * Math.PI / 180)
      }
      
      const x1 = center.x + radius * Math.cos(startAngleRad)
      const y1 = center.y + radius * Math.sin(startAngleRad)
      const x2 = center.x + radius * Math.cos(endAngleRad)
      const y2 = center.y + radius * Math.sin(endAngleRad)
      
      const largeArc = curve.span > 180 ? 1 : 0
      const sweepFlag = shouldFlip ? 0 : 1
      
      return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} ${sweepFlag} ${x2} ${y2}`
    }
  }
}
</script>
